import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../api.js';
import { t } from '../i18n/index.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import type { ArenaSocket } from '../realtime.js';
import type { ArenaResult, ChatMessage, ModLogEntry, ModUserView, PublicUser, RestrictionType } from '../../shared/protocol.js';

type Flash = { ok: boolean; text: string } | null;
const errorText = (err: unknown) => t.errors[err instanceof ApiError ? err.code : 'network'];
const dateTime = (ms: number) => new Date(ms).toLocaleString('fi-FI', { dateStyle: 'short', timeStyle: 'short' });
const DURATIONS = [60, 1440, 10080, 43200];

/** Moderation for moderators and the administrator (suunnitelma.md section 26). */
export function ModerationView(props: { me: PublicUser; socket: ArenaSocket | null }) {
  const isAdmin = props.me.role === 'admin';
  const [users, setUsers] = useState<ModUserView[]>([]);
  const [log, setLog] = useState<ModLogEntry[]>([]);
  const [open, setOpen] = useState<boolean | null>(null);
  const [flash, setFlash] = useState<Flash>(null);
  const [confirmClear, setConfirmClear] = useState(false);

  const [userId, setUserId] = useState('');
  const [type, setType] = useState<RestrictionType>('chat_mute');
  const [duration, setDuration] = useState<string>('60');
  const [reason, setReason] = useState('');

  const load = async () => {
    try {
      const [u, l] = await Promise.all([
        api<{ users: ModUserView[] }>('GET', '/api/mod/users'),
        api<{ entries: ModLogEntry[] }>('GET', '/api/mod/log'),
      ]);
      setUsers(u.users);
      setLog(l.entries);
    } catch (err) {
      setFlash({ ok: false, text: errorText(err) });
    }
  };

  useEffect(() => {
    void load();
    props.socket?.emit('lobby:history', (r: ArenaResult<{ messages: ChatMessage[]; open: boolean }>) => {
      if (r.ok) setOpen(r.open);
    });
  }, []);

  const run = async (fn: () => Promise<unknown>, okText?: string) => {
    try {
      await fn();
      if (okText) setFlash({ ok: true, text: okText });
      await load();
    } catch (err) {
      setFlash({ ok: false, text: errorText(err) });
    }
  };

  const toggleChat = () =>
    run(async () => {
      const r = await api<{ open: boolean }>('POST', '/api/mod/lobby/state', { open: !open });
      setOpen(r.open);
    });

  const clearChat = () =>
    run(async () => {
      setConfirmClear(false);
      const r = await api<{ deleted: number }>('POST', '/api/admin/lobby/clear');
      setFlash({ ok: true, text: t.moderation.cleared(r.deleted) });
    });

  const addRestriction = (e: Event) => {
    e.preventDefault();
    void run(
      () =>
        api('POST', '/api/mod/restrictions', {
          userId: Number(userId),
          type,
          durationMinutes: duration === 'permanent' ? null : Number(duration),
          reason,
        }),
      t.moderation.added,
    ).then(() => setReason(''));
  };

  // Moderators can restrict players only; the administrator anyone except themselves.
  const targets = users.filter((u) => u.id !== props.me.id && (isAdmin || u.role === 'player'));
  // Moderators can lift restrictions of players only, not their own (checked by the server too).
  const canRevoke = (userId: number) => isAdmin || targets.some((u) => u.id === userId);
  const active = users.flatMap((u) => u.restrictions);

  return (
    <div class="stack">
      <section class="card">
        <div class="section-head">
          <h2>{t.moderation.chatTitle}</h2>
          <div class="user-actions">
            {open !== null && (
              <button class="btn btn-small" onClick={() => void toggleChat()}>
                {open ? t.lobbyChat.closeChat : t.lobbyChat.openChat}
              </button>
            )}
            {isAdmin && (
              <button class="btn btn-small btn-danger" onClick={() => setConfirmClear(true)}>
                {t.moderation.clearChat}
              </button>
            )}
          </div>
        </div>
        {open !== null && <p class="muted">{open ? t.moderation.chatOpen : t.moderation.chatClosed}</p>}
        {flash && (
          <p class={flash.ok ? 'success' : 'error'} role="status">
            {flash.text}
          </p>
        )}
      </section>

      <form class="card form" onSubmit={addRestriction}>
        <h2>{t.moderation.restrictTitle}</h2>
        <div class="grid-2">
          <label>
            {t.moderation.player}
            <select value={userId} onChange={(e) => setUserId(e.currentTarget.value)} required>
              <option value="">{t.moderation.choosePlayer}</option>
              {targets.map((u) => (
                <option value={String(u.id)}>
                  {u.displayName} (@{u.username})
                </option>
              ))}
            </select>
          </label>
          <label>
            {t.moderation.type}
            <select
              value={type}
              onChange={(e) => {
                const v = e.currentTarget.value as RestrictionType;
                setType(v);
                if (v === 'game_ban' && duration === 'permanent') setDuration('1440');
              }}
            >
              <option value="chat_mute">{t.moderation.types.chat_mute}</option>
              <option value="game_ban">{t.moderation.types.game_ban}</option>
            </select>
          </label>
          <label>
            {t.moderation.duration}
            <select value={duration} onChange={(e) => setDuration(e.currentTarget.value)}>
              {DURATIONS.map((d) => (
                <option value={String(d)}>{t.moderation.durations[d]}</option>
              ))}
              {type === 'chat_mute' && <option value="permanent">{t.moderation.permanent}</option>}
            </select>
          </label>
          <label>
            {t.moderation.reason}
            <input value={reason} maxLength={200} onInput={(e) => setReason(e.currentTarget.value)} />
          </label>
        </div>
        <button class="btn btn-primary" type="submit" disabled={!userId}>
          {t.moderation.add}
        </button>

        <h3>{t.moderation.active}</h3>
        {active.length === 0 ? (
          <p class="muted">{t.moderation.none}</p>
        ) : (
          <ul class="user-list">
            {active.map((r) => (
              <li class="user-row">
                <div class="user-main">
                  <div class="user-name">
                    {r.user.displayName} · {t.moderation.types[r.type]}
                  </div>
                  <div class="user-sub">
                    {r.until === null ? t.moderation.forever : t.moderation.until(dateTime(r.until))} · {t.moderation.by(r.createdBy ?? t.moderation.deletedUser)}
                    {r.reason && ` · ${r.reason}`}
                  </div>
                </div>
                {canRevoke(r.user.id) && (
                  <button
                    class="btn btn-small"
                    type="button"
                    onClick={() => void run(() => api('POST', `/api/mod/restrictions/${r.id}/revoke`), t.moderation.revoked)}
                  >
                    {t.moderation.revoke}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </form>

      <section class="card">
        <h2>{t.moderation.logTitle}</h2>
        {log.length === 0 ? (
          <p class="muted">{t.moderation.logEmpty}</p>
        ) : (
          <ul class="mod-log">
            {log.map((e) => (
              <li>
                <span class="muted">{dateTime(e.at)}</span> <strong>{e.actor}</strong>: {t.moderation.actions[e.action] ?? e.action}
                {e.target && ` → ${e.target}`}
                {e.details && <span class="muted"> · {e.details}</span>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {confirmClear && (
        <ConfirmDialog
          title={t.moderation.clearConfirmTitle}
          confirmLabel={t.moderation.clearChat}
          cancelLabel={t.admin.cancel}
          danger
          onConfirm={() => void clearChat()}
          onCancel={() => setConfirmClear(false)}
        >
          <p>{t.moderation.clearConfirmText}</p>
        </ConfirmDialog>
      )}
    </div>
  );
}
